import { useState } from 'react';
import { Landmark, ArrowUpCircle, Loader2, ShieldCheck, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * In-flow upsell shown when the user hits their plan's Plaid bank-connection
 * limit. Two options, each a 2-click authorize (pick → confirm):
 *   • +1 bank connection ($5/mo) — added to the existing subscription instantly
 *   • Upgrade to the next tier — swapped on the existing subscription instantly
 * Falls back to Stripe Checkout redirect when no card is on file.
 *
 * Props:
 *   open / onOpenChange — dialog state
 *   limits  — { used, limit, planName }
 *   upsell  — { addon_price, next_plan: {id, name, price, plaid_account_limit, monthly_credits} | null }
 *   onUnlocked(result) — called after an instant unlock so the caller can
 *                        refresh limits and auto-continue the Plaid flow
 */
export function BankLimitUpsellModal({ open, onOpenChange, limits, upsell, onUnlocked }) {
  const [confirming, setConfirming] = useState(null); // null | 'addon' | 'upgrade'
  const [busy, setBusy] = useState(false);

  const addonPrice = upsell?.addon_price ?? 5;
  const nextPlan = upsell?.next_plan || null;

  const authorize = async (action) => {
    setBusy(true);
    try {
      const r = await apiFetch(`${API_URL}/api/billing/bank-boost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          plan_id: action === 'upgrade' ? nextPlan?.id : undefined,
          origin_url: window.location.origin,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data.detail || 'Could not process the upgrade. Try again.');
        return;
      }
      if (data.success && data.method === 'instant') {
        toast.success(data.message || 'Unlocked! Connecting your bank…');
        onOpenChange(false);
        setConfirming(null);
        onUnlocked?.(data);
        return;
      }
      if (data.url) {
        // No card on file — finish authorization on Stripe Checkout.
        window.location.href = data.url;
        return;
      }
      toast.error('Unexpected response — try again.');
    } catch {
      toast.error('Network error — try again.');
    } finally {
      setBusy(false);
    }
  };

  const confirmCopy = confirming === 'addon'
    ? {
        title: 'Add +1 bank connection',
        line: `You authorize $${Number(addonPrice).toFixed(2)}/mo to be added to your existing CountBucks subscription (prorated today, on your card on file).`,
        cta: `Confirm — $${Number(addonPrice).toFixed(2)}/mo`,
      }
    : {
        title: `Upgrade to ${nextPlan?.name}`,
        line: `You authorize your subscription to change to ${nextPlan?.name} at $${Number(nextPlan?.price || 0).toFixed(2)}/mo (prorated today, on your card on file).`,
        cta: `Confirm upgrade — $${Number(nextPlan?.price || 0).toFixed(2)}/mo`,
      };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { onOpenChange(o); if (!o) setConfirming(null); } }}>
      <DialogContent className="sm:max-w-md" data-testid="bank-limit-upsell-modal">
        {!confirming ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <Landmark className="w-4 h-4 text-emerald-500" />
                Add another bank connection
              </DialogTitle>
              <DialogDescription className="text-xs">
                You've used {limits?.used ?? 0} of {limits?.limit ?? 0} bank{' '}
                {(limits?.limit ?? 0) === 1 ? 'connection' : 'connections'} on{' '}
                {limits?.planName || 'your plan'}. Unlock more in two clicks — then
                we'll open your bank connection right away.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 mt-1">
              <button
                type="button"
                onClick={() => setConfirming('addon')}
                className="w-full p-4 rounded-xl border-2 border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/20 hover:border-emerald-500 transition-all text-left group"
                data-testid="bank-boost-addon-option"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                      <Landmark className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-zinc-900 dark:text-white">+1 bank connection</p>
                      <p className="text-[11px] text-zinc-500">Keep your current plan, just add a slot</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">${Number(addonPrice).toFixed(2)}</p>
                    <p className="text-[10px] text-zinc-500">/month</p>
                  </div>
                </div>
              </button>

              {nextPlan && (
                <button
                  type="button"
                  onClick={() => setConfirming('upgrade')}
                  className="w-full p-4 rounded-xl border border-zinc-200 dark:border-white/10 hover:border-violet-500/50 hover:bg-violet-50/40 dark:hover:bg-violet-950/10 transition-all text-left"
                  data-testid="bank-boost-upgrade-option"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                        <ArrowUpCircle className="w-4.5 h-4.5 text-violet-600 dark:text-violet-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-zinc-900 dark:text-white">Upgrade to {nextPlan.name}</p>
                        <p className="text-[11px] text-zinc-500">
                          {nextPlan.plaid_account_limit} bank connections
                          {nextPlan.monthly_credits ? ` · ${nextPlan.monthly_credits.toLocaleString()} AI credits/mo` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-zinc-900 dark:text-white">${Number(nextPlan.price).toFixed(0)}</p>
                      <p className="text-[10px] text-zinc-500">/month</p>
                    </div>
                  </div>
                </button>
              )}
            </div>

            <p className="text-[10px] text-zinc-400 dark:text-zinc-600 flex items-center gap-1.5 mt-1">
              <ShieldCheck className="w-3 h-3 flex-shrink-0" />
              Billed to your card on file · prorated · cancel or downgrade anytime in Settings → Billing
            </p>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-base">{confirmCopy.title}</DialogTitle>
              <DialogDescription className="text-xs leading-relaxed">{confirmCopy.line}</DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-9"
                disabled={busy}
                onClick={() => setConfirming(null)}
                data-testid="bank-boost-back"
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
              <Button
                className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                disabled={busy}
                onClick={() => authorize(confirming)}
                data-testid="bank-boost-confirm"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmCopy.cta}
              </Button>
            </div>
            <p className="text-[10px] text-zinc-400 dark:text-zinc-600 mt-1">
              After confirming, your bank connection window opens automatically.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default BankLimitUpsellModal;
